import { Injectable } from '@angular/core';
import { CommunicationManagerService } from '../services/communication-manager.service';
import { CostItem } from '../models/cost-item';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CostItemService {
  //TODO: see about injecting this in constructor like in media service
  //TODO: stop using cached projectId for server calls
  private projectId: string;
  private projectItemsSub: Observable<any> | undefined;

  constructor(private cm: CommunicationManagerService) {
    this.projectId = '';
  }

  public getCostItemsByGroup(groupId: string, projectId: string) {
    this.projectId = projectId;
    return this.cm.get(`costItem/all/${groupId}`, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  public getCostItemsByProject(projectId: string) {
    this.projectId = projectId;
    this.projectItemsSub = this.cm.get('costItem/all/project', [
      { headerName: 'projectId', headerValue: projectId },
    ]);
    return this.projectItemsSub;
  }

  getCostItemsLoadedWithEstimateItemsValues(projectId: string) {
    return this.cm.get(`costItem/all/project/withMatchedTotal`, [
      { headerName: 'projectId', headerValue: projectId }
    ]);
  }

  public updateCostItemMultipleOrderNumbers(
    projectId?: string,
    costItems?: Array<{ id: string; orderNumber: number }>
  ) {
    return this.cm.put(`costItem/multi`, costItems, [
      { headerName: 'projectId', headerValue: projectId ?? this.projectId },
    ]);
  }

  public updateItemOrder(
    projectId: string,
    items: { id: string; orderNumber: number }[]
  ): Observable<any> {
    return this.cm.put(`costItem/multi`, items, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  

  public createCostItemByGroup(
    groupId: string,
    item: CostItem,
    projectId: string,
    companyId: string
  ) {
    this.projectId = projectId;
    const itemDto: any = {
      position: item.position,
      name: item.name,
      description: item.description,
      units: item.units ?? 0,
      cost: item.cost ?? 0,
      days: item.days ?? 0,
      hasReminder: item.hasReminder,
      paymentMethod: item.paymentMethod,
      paymentDate: item.paymentDate
        ? new Date(item.paymentDate)?.toLocaleDateString('en-US')
        : null,
      paidBy: item.paidBy,
      accountingCategory: item.accountingCategory,
      costStatus: item.costStatus,
      dateOfBill: item.dateOfBill
        ? new Date(item.dateOfBill)?.toLocaleDateString('en-US')
        : null,
      assignedTo: item.assignedTo,
      costType: item.costType,
      orderNumber: item.orderNumber,
      color: item.color,
      taxPercentage: item.taxPercentage,
      currencyCode: item.currencyCode,
      exchangeRate: item.exchangeRate,
    };

    Object.keys(itemDto).forEach((field) =>
      itemDto[field] == undefined ? delete itemDto[field] : {}
    );

    return this.cm.postAndGetJson(`costItem/${groupId}`, itemDto, [
      { headerName: 'projectId', headerValue: projectId },
      { headerName: 'companyId', headerValue: companyId },
    ]);
  }

  public createMultipleCostItemsByGroup(
    projectId: string,
    groupId: string,
    positions: Array<string>,
    startingOrderNumber?: number
  ) {
    this.projectId = projectId;
    return this.cm.postAndGetJson(
      `costItem/multi/${groupId}`,
      {
        positions: positions,
        startingOrderNumber: startingOrderNumber,
      },
      [{ headerName: 'projectId', headerValue: projectId }]
    );
  }

  public updateCostItem(item: CostItem, projectId: string) {
    const itemDto: any = {
      position: item.position,
      name: item.name,
      description: item.description,
      units: item.units ?? 0,
      cost: item.cost ?? 0,
      days: item.days ?? 0,
      hasReminder: item.hasReminder,
      paymentMethod: item.paymentMethod,
      paymentDate: item.paymentDate
        ? new Date(item.paymentDate)?.toLocaleDateString('en-US')
        : null,
      paidBy: item.paidBy,
      accountingCategory: item.accountingCategory,
      costStatus: item.costStatus,
      dateOfBill: item.dateOfBill
        ? new Date(item.dateOfBill)?.toLocaleDateString('en-US')
        : null,
      assignedTo: item.assignedTo,
      costType: item.costType,
      orderNumber: item.orderNumber,
      color: item.color,
      taxPercentage: item.taxPercentage,
      currencyCode: item.currencyCode,
      exchangeRate: item.exchangeRate,
    };

    Object.keys(itemDto).forEach((field) =>
      itemDto[field] == undefined ? delete itemDto[field] : {}
    );

    delete itemDto.editedBy;

    return this.cm.put(`costItem/${item._id}`, itemDto, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  updateCostItemMultiple(items: Array<CostItem>, projectId: string) {
    return this.cm.put(`costItem/multi`, items, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  cloneCostItems(itemIds: Array<string>, projectId: string) {
    return this.cm.postAndGetJson(`costItem/clone`, { ids: itemIds }, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  moveCostItemToDepartment(itemId: string, departmentId: string, projectId: string) {
    return this.cm.put(`costItem/${itemId}`, { itemGroup: departmentId }, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  moveCostItemMultiple(items: Array<CostItem>, departmentId: string, projectId: string) {
    const payload = items.map(item => {
      return {
        id: item._id,
        itemGroup: departmentId
      };
    });
    return this.cm.put(`costItem/multi`, payload, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  deleteCostItem(itemId: string, projectId: string) {
    return this.cm.delete(`costItem/${itemId}`, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  deleteCostItemMultiple(itemIds: Array<string>, projectId: string) {
    const payload = { ids: itemIds };
    return this.cm.deleteMultiple(`costItem/multiple`, payload, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  attachFiles(item: CostItem, type: 'invoice' | 'payment', files: Array<File>) {
    const payload: FormData = new FormData();

    Array.from(files).forEach((f) => {
      payload.append(type, f);
    });

    return this.cm.uploadAndGetJson({
      route: `costItem/attachFile/${item._id}`,
      payload,
      addHeaders: [
        { headerName: 'projectId', headerValue: item.project || '' },
      ],
    });
  }

  downloadFile(item: CostItem, fileId: string) {
    // console.log('item', item);
    return this.cm.downloadFile(`costItem/downloadFile/${item._id}/${fileId}`, [
      { headerName: 'projectId', headerValue: item.project || '' },
    ]);
  }

  removeFile(item: CostItem, fileId: string) {
    return this.cm.postAndGetJson(
      `costItem/removeFile/${item._id}/${fileId}`,
      null,
      [{ headerName: 'projectId', headerValue: item.project || '' }]
    );
  }

  previewFile(item: CostItem, fileId: string) {
    return this.cm.previewFile(`costItem/previewFile/${item._id}/${fileId}`, [
      { headerName: 'projectId', headerValue: item.project || '' },
    ]);
  }
}
